import { defineStore, acceptHMRUpdate } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useScanPollingQueries } from 'src/composables/scans/query/useScanPollingQueries'
import { getScanStatusQueryOptions } from 'src/composables/scan/query/useScanResultQuery'
import { broadcastService } from 'src/services/app/broadcast.service'
import { localStorageService } from 'src/services/app/storage.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import {
  BROADCAST_TOPICS,
  BROADCAST_UPDATE_TYPES,
  CACHE_ACTIONS,
} from 'src/services/consts/app.const'
import { SCAN_STATUS } from 'src/services/consts/shared.const'
import { isActiveScanStatus, isScanInProgress } from 'src/services/helpers/scan/status.helper'
import { useAccountStore } from 'src/stores/account'

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

/**
 * Global **scan registry** store — polling queries, localStorage persistence,
 * and cross-tab broadcast sync for in-progress scans.
 *
 * Pinia id `'banners-scan-registry'` is distinct from `'banner-scan'`, `'banner-scan-history'`,
 * and `'banner-detail'`.
 */
export const useScanRegistryStore = defineStore('banners-scan-registry', () => {
  const accountStore = useAccountStore()
  const queryClient = useQueryClient()
  const scanRegistry = ref({})

  const registrySiteIds = computed(() => Object.keys(scanRegistry.value))

  const { scanStatusMap } = useScanPollingQueries(registrySiteIds)

  // Keyed by siteId, not scanId — a preview auto-chaining into a full scan is one
  // registry entry throughout, never two. `scanType` is exposed so consumers (e.g.
  // ScanStatusPreview) can tell "preview completed, full scan about to take over"
  // apart from a scan that's actually finished, instead of flashing a completed
  // state between the two legs of the chain.
  const indicatorScans = computed(() =>
    Object.entries(scanRegistry.value).map(([siteId, meta]) => {
      const query = scanStatusMap.value[siteId]
      const data = query?.data
      const isLoading = query?.isLoading ?? true
      const hasFetchError = !!query?.error
      const cachedStatus = data?.status
      const status =
        hasFetchError && (!cachedStatus || isActiveScanStatus(cachedStatus))
          ? SCAN_STATUS.FAILED
          : (cachedStatus ?? SCAN_STATUS.PENDING)
      return {
        siteId,
        siteName: meta.siteName,
        domain: meta.domain ?? null,
        status,
        scanType: data?.scanType ?? null,
        pagesScanned: data?.pagesScanned ?? null,
        totalPagesFound: data?.totalPagesFound ?? null,
        error: data?.error ?? null,
        summary: data?.results?.summary ?? null,
        isLoading,
      }
    })
  )

  watch(
    () => accountStore.accountId,
    (accountId) => {
      if (!accountId) return
      restoreScans(accountId)
    },
    { immediate: true }
  )

  function persistRegistry() {
    localStorageService.set(STORAGE_KEYS.SCAN_REGISTRY, {
      accountId: accountStore.accountId ?? null,
      scans: scanRegistry.value,
    })
  }

  function postScanRegistryBroadcast({ action, siteId, meta }) {
    broadcastService.post(BROADCAST_TOPICS.DATA_SYNC, {
      updateType: BROADCAST_UPDATE_TYPES.SCAN_REGISTRY,
      action,
      siteId,
      meta,
    })
  }

  function isOlderThan24Hours(meta) {
    if (!meta.startedAt) return false
    return Date.now() - new Date(meta.startedAt).getTime() > TWENTY_FOUR_HOURS_MS
  }

  async function restoreScans(accountId) {
    const stored = localStorageService.get(STORAGE_KEYS.SCAN_REGISTRY)
    if (!stored?.scans || stored.accountId !== accountId) {
      return
    }

    const kept = {}
    await Promise.all(
      Object.entries(stored.scans).map(async ([siteId, meta]) => {
        try {
          // Use the shared SCAN_STATUS query so restore dedupes with tab/drawer observers
          const result = await queryClient.fetchQuery(
            getScanStatusQueryOptions({ queryClient, siteId })
          )
          if (result?.status === SCAN_STATUS.COMPLETED && isOlderThan24Hours(meta)) {
            return
          }
          kept[siteId] = meta
        } catch (error) {
          // 404 = site deleted — drop. Keep on transient errors so polling can retry.
          if (error?.response?.status === 404) return
          kept[siteId] = meta
        }
      })
    )

    scanRegistry.value = { ...kept, ...scanRegistry.value }
    persistRegistry()
  }

  function startScan({ siteId, siteName, domain, scanId, startedAt }, { broadcast = true } = {}) {
    const meta = {
      siteName,
      domain,
      scanId,
      startedAt: startedAt ?? new Date().toISOString(),
    }
    const rest = { ...scanRegistry.value }
    delete rest[siteId]
    scanRegistry.value = { [siteId]: meta, ...rest }
    persistRegistry()
    if (broadcast) {
      postScanRegistryBroadcast({ action: CACHE_ACTIONS.ADD, siteId, meta })
    }
  }

  /** Enroll an in-progress scan discovered outside triggerScan (e.g. backend auto-scan). */
  function ensureActiveScanTracked({ siteId, siteName, domain, scanId, status, scanType }) {
    if (!siteId || !isScanInProgress({ status, scanType }) || scanRegistry.value[siteId]) return

    startScan({ siteId, siteName, domain, scanId })
  }

  function dismissScan(siteId, { broadcast = true } = {}) {
    if (!scanRegistry.value[siteId]) return

    const next = { ...scanRegistry.value }
    delete next[siteId]
    scanRegistry.value = next
    persistRegistry()
    if (broadcast) {
      postScanRegistryBroadcast({ action: CACHE_ACTIONS.REMOVE, siteId })
    }
  }

  function applyScanRegistryBroadcast({ action, siteId, meta }) {
    if (!siteId) return

    if (action === CACHE_ACTIONS.ADD && meta) {
      startScan({ siteId, ...meta }, { broadcast: false })
      return
    }

    if (action === CACHE_ACTIONS.REMOVE) {
      dismissScan(siteId, { broadcast: false })
    }
  }

  function resetState() {
    scanRegistry.value = {}
    localStorageService.remove(STORAGE_KEYS.SCAN_REGISTRY)
  }

  return {
    indicatorScans,
    startScan,
    ensureActiveScanTracked,
    applyScanRegistryBroadcast,
    dismissScan,
    resetState,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useScanRegistryStore, import.meta.hot))
}
